import { IControlDefinition } from "Grids/Interfaces";
import { IFileUpload } from "Controls/Interfaces";
import { Control } from "Controls/Control";
import { TextBlockService, CommandService } from "Service";
import { IFileUploadParameter } from "Entities";
import { CoreContext } from "Core";

/**
 * ## FileUpload
 *
 * Control for file upload.
 * The Textblocks used in this control are in the TextblockCategory 'fileupload'.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of an CustomControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class FileUploadControl extends Control implements IFileUpload {
    private _kendoElement: kendo.ui.Upload;

    private commandService: CommandService;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-fileupload";

    /**
     * @hidden
     */
    protected bindingProperty: string = null;

    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<input type="file" name="files"/>`;

    /**
     * Create a new FileUploadControl.
     * @param controlDefinition
     * @param textBlockService
     */
    constructor(
        controlDefinition: IControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        commandService: CommandService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService);
        this.commandService = commandService;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     */
    protected setElementInternal(element: JQuery): void {
        this._kendoElement = new kendo.ui.Upload(this.jQueryElement[0], {
            async: {
                saveUrl: "FileUpload/Save",
                removeUrl: "FileUpload/Remove"
            },
            multiple: false,
            upload: e => this.onUpload(e),
            remove: e => this.onRemove(e),
            validation: {
                allowedExtensions: CoreContext.Environment.FileUploadExtensionWhitelist || []
            },
            localization: {
                select: "&nbsp;"
            }
        });

        // the fileuploadcontrol has many textblocks but the control itself is rarely used.
        // therefore the textblocks are loaded on initialization.
        this.textBlockService.load(CoreContext.Environment.LanguageCode, "fileupload").then(tb => {
            let textblocks = tb["fileupload"];
            (<any>this._kendoElement).localization = {
                select: this.textBlockService.getChecked(textblocks, "Select", "fileupload"),
                cancel: this.textBlockService.getChecked(textblocks, "Cancel", "fileupload"),
                remove: this.textBlockService.getChecked(textblocks, "Remove", "fileupload"),
                dropFilesHere: this.textBlockService.getChecked(textblocks, "DropFilesHere", "fileupload"),
                headerStatusUploaded: this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload"),
                headerStatusUploading: this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload"),
                statusFailed: this.textBlockService.getChecked(textblocks, "StatusFailed", "fileupload"),
                statusUploaded: this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload"),
                statusUploading: this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload"),
                invalidFileExtension: this.textBlockService.getChecked(textblocks, "InvalidFileExtension", "fileupload")
            };
            // since the localization of the already rendered element is not updated when setting localization,
            // the textblocks of the button / dropzone is set manually.
            this._kendoElement.wrapper.find(".k-button > span").html(this.textBlockService.getChecked(textblocks, "Select", "fileupload"));
            this._kendoElement.wrapper
                .find(".k-dropzone > em")
                .html(this.textBlockService.getChecked(textblocks, "DropFilesHere", "fileupload"));
        });
    }

    private onRemove(e: kendo.ui.UploadRemoveEvent): void {
        this.setRequestParameters(e);
    }

    private onUpload(e: kendo.ui.UploadEvent): void {
        this.setRequestParameters(e);
    }

    private setRequestParameters(e: kendo.ui.UploadEvent | kendo.ui.UploadRemoveEvent) {
        let param = <IFileUploadParameter>this.commandService.getContextParameters();
        param.ControlID = this.controlDefinition.ControlID;
        (<any>e).data = param;
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement != null ? this._kendoElement.wrapper : this.jQueryElement;
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        return ``;
    }

    /**
     * Get the kendo upload control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/upload|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.Upload {
        return this._kendoElement;
    }

    /**
     * Not implemented for this control type.
     * @param value
     * @hidden
     */
    public setValue(value: string): void {}

    /**
     * Gets the file name of the selected file or null if no file was selected.
     * @since 4.0.0
     */
    public getValue(): string {
        let files = this._kendoElement.getFiles();
        return files.length > 0 ? files[0].name : null;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
    }
}
